/**
 * Shared controls — buttons, inputs, selects, checkboxes, modal shell.
 *
 * Thin wrappers over the `.nl-btn` / `.nl-input` / `.nl-select` classes in
 * src/index.css, which carry the actual look (design tokens, so dark mode
 * follows). Prefer these in new screens; `btn()` is there for the places that
 * need the classes on something other than a <button>, e.g. a <label> that
 * wraps a file input.
 */
import React from 'react';

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'soft'
  | 'ghost'
  | 'danger'
  | 'danger-outline';
export type ButtonSize = 'md' | 'sm' | 'xs';

const cx = (...parts: Array<string | false | null | undefined>) =>
  parts.filter(Boolean).join(' ');

export const btn = (variant: ButtonVariant = 'primary', size: ButtonSize = 'md') =>
  cx('nl-btn', `nl-btn--${variant}`, size !== 'md' && `nl-btn--${size}`);

type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
};

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({variant = 'primary', size = 'md', className, type = 'button', ...rest}, ref) => (
    <button ref={ref} type={type} className={cx(btn(variant, size), className)} {...rest} />
  ),
);
Button.displayName = 'Button';

type InputProps = React.InputHTMLAttributes<HTMLInputElement> & {inputSize?: 'md' | 'sm'};

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({className, inputSize = 'md', ...rest}, ref) => (
    <input
      ref={ref}
      className={cx('nl-input', inputSize === 'sm' && 'nl-input--sm', className)}
      {...rest}
    />
  ),
);
Input.displayName = 'Input';

export const Textarea = React.forwardRef<
  HTMLTextAreaElement,
  React.TextareaHTMLAttributes<HTMLTextAreaElement>
>(({className, ...rest}, ref) => (
  <textarea ref={ref} className={cx('nl-input', className)} {...rest} />
));
Textarea.displayName = 'Textarea';

type SelectProps = React.SelectHTMLAttributes<HTMLSelectElement> & {inputSize?: 'md' | 'sm'};

export const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
  ({className, inputSize = 'md', ...rest}, ref) => (
    <select
      ref={ref}
      className={cx('nl-select', inputSize === 'sm' && 'nl-input--sm', className)}
      {...rest}
    />
  ),
);
Select.displayName = 'Select';

/** Label above a control. */
export const Field = ({
  label,
  className,
  children,
}: {
  label: React.ReactNode;
  className?: string;
  children: React.ReactNode;
}) => (
  <label className={cx('block', className)}>
    <span className="nl-label">{label}</span>
    {children}
  </label>
);

/** Checkbox with its label, aligned to sit in a row of 40px controls. */
export const Checkbox = ({
  label,
  className,
  ...rest
}: React.InputHTMLAttributes<HTMLInputElement> & {label: React.ReactNode}) => (
  <label
    className={cx(
      'inline-flex h-10 cursor-pointer select-none items-center gap-2 text-sm text-text2',
      className,
    )}
  >
    <input type="checkbox" className="nl-check" {...rest} />
    {label}
  </label>
);

/** Backdrop + panel for hand-rolled dialogs. */
export const ModalShell = ({
  className,
  children,
}: {
  className?: string;
  children: React.ReactNode;
}) => (
  <div className="nl-backdrop">
    <div className={cx('nl-modal', className ?? 'max-w-md')}>{children}</div>
  </div>
);

/** Stacking for dialogs and the dropdowns inside them. Kept in step with the
 *  `.ReactModal__Overlay` rule in src/index.css. */
export const Z_MODAL = 1000;
export const Z_MODAL_MENU = 1100;

/**
 * Props for a react-select that lives inside a modal or drawer: the menu is
 * portalled to <body> so the dialog's overflow can't clip it, and sits above
 * every modal overlay. Spread in place of `styles`:
 * `<Select {...portalMenu(selectStyles)} … />`.
 */
export const portalMenu = (styles: Record<string, any> = {}) => ({
  menuPortalTarget: typeof document !== 'undefined' ? document.body : null,
  styles: {
    ...styles,
    menuPortal: (base: any) => ({...base, zIndex: Z_MODAL_MENU}),
  },
});
